import React, { useState, useEffect, useCallback } from 'react';
import { Eye, UserX, UserCheck, Search, Filter, Plus, X, Package } from 'lucide-react';
import { vendorApi } from '../services/api';
import { Vendor } from '../types';
import VendorDetailsModal from './VendorDetailsModal';
import VendorCreateModal from './VendorCreateModal';
import { extractApiError } from '../utils/errorUtils';

export default function VendorManagement() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<number | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filters
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVendors, setTotalVendors] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Confirmation states
  const [deactivateConfirm, setDeactivateConfirm] = useState<number | null>(null);
  const [activateConfirm, setActivateConfirm] = useState<number | null>(null);

  const loadVendors = useCallback(async () => {
    try {
      setLoading(true);

      // Build API parameters with server-side filtering
      const params: {
        search?: string;
        product_types?: string;
        include_inactive?: boolean;
        page?: number;
        page_size?: number;
      } = {
        include_inactive: includeInactive,
        page: currentPage,
        page_size: pageSize
      };

      // Add search parameter if provided
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add product_types as comma-separated string if provided
      if (selectedProductTypes.length > 0) {
        params.product_types = selectedProductTypes.join(',');
      }

      const response = await vendorApi.adminGetVendors(params);
      setVendors(response.items);
      setTotalPages(response.total_pages);
      setTotalVendors(response.total);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load vendors:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [includeInactive, currentPage, pageSize, searchTerm, selectedProductTypes]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, selectedProductTypes, includeInactive, currentPage]);

  const handleDeactivate = async (vendorId: number) => {
    try {
      setActionError(null);
      await vendorApi.adminDeactivateVendor(vendorId);
      setDeactivateConfirm(null);
      loadVendors(); // Reload to reflect changes
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to deactivate vendor:', apiError);
      setActionError(apiError.message);
      setDeactivateConfirm(null);
    }
  };

  const handleActivate = async (vendorId: number) => {
    try {
      setActionError(null);
      await vendorApi.adminUpdateVendor(vendorId, { is_active: true });
      setActivateConfirm(null);
      loadVendors(); // Reload to reflect changes
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to activate vendor:', apiError);
      setActionError(apiError.message);
      setActivateConfirm(null);
    }
  };

  const handleViewDetails = (vendorId: number) => {
    setSelectedVendor(vendorId);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedVendor(null);
    loadVendors(); // Reload in case changes were made
  };

  // Common product types for filtering (predefined list since we use server-side filtering)
  const allProductTypes = [
    'Jewelry',
    'Pottery',
    'Textiles',
    'Woodwork',
    'Metalwork',
    'Glass',
    'Leather',
    'Candles',
    'Soaps',
    'Paintings',
    'Photography',
    'Home Decor',
    'Clothing',
    'Food & Beverages'
  ].sort();

  if (loading && vendors.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading vendors...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error}</p>
        <button
          onClick={loadVendors}
          className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-full overflow-hidden">
      {/* Action Error Display */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start justify-between">
          <p className="text-sm text-red-700">{actionError}</p>
          <button
            onClick={() => setActionError(null)}
            className="text-red-400 hover:text-red-600"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Vendor Management</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and moderate vendor profiles
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add New Vendor</span>
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white shadow-sm rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Vendors</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalVendors}</p>
              </div>
              <div className="h-12 w-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-secondary" />
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Vendors</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {vendors.filter(v => v.is_active).length}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Inactive Vendors</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">
                  {vendors.filter(v => !v.is_active).length}
                </p>
              </div>
              <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <UserX className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white shadow-sm rounded-lg p-4 space-y-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search by Business Name, User ID, or Email
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search vendors..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setIncludeInactive(e.target.checked);
                  setCurrentPage(1); // Reset to first page
                }}
                className="rounded border-gray-300 text-secondary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Include Inactive Vendors</span>
            </label>
          </div>

          {/* Product Types Filter */}
          {allProductTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Product Types
              </label>
              <div className="flex flex-wrap gap-2">
                {allProductTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelectedProductTypes(prev =>
                        prev.includes(type)
                          ? prev.filter(t => t !== type)
                          : [...prev, type]
                      );
                    }}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedProductTypes.includes(type)
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-pressed={selectedProductTypes.includes(type)}
                    aria-label={`Filter by ${type}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              {selectedProductTypes.length > 0 && (
                <button
                  onClick={() => setSelectedProductTypes([])}
                  className="mt-2 text-sm text-secondary hover:text-primary-dark font-medium"
                >
                  Clear Product Type Filters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Vendor List */}
      {vendors.length === 0 ? (
        <div className="bg-white shadow-sm rounded-lg p-8 text-center">
          <p className="text-gray-500">No vendors found matching your filters.</p>
          {(searchTerm || selectedProductTypes.length > 0) && (
            <>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedProductTypes([]);
                }}
                className="mt-4 text-secondary hover:text-primary-dark font-medium"
              >
                Clear All Filters
              </button>
              {totalPages > 1 && currentPage < totalPages && (
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="mt-2 ml-4 text-secondary hover:text-primary-dark font-medium"
                >
                  Try Next Page
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product Types
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendors.map((vendor) => (
                    <tr key={vendor.id ?? `user-${vendor.user_id}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {vendor.business_name}
                        </div>
                        {vendor.description && (
                          <div className="text-xs text-gray-500 line-clamp-1">
                            {vendor.description}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {vendor.product_types?.slice(0, 3).map((type, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                            >
                              {type}
                            </span>
                          ))}
                          {vendor.product_types && vendor.product_types.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              +{vendor.product_types.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          vendor.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium space-x-2">
                        {vendor.id ? (
                          <>
                            <button
                              onClick={() => handleViewDetails(vendor.id!)}
                              className="text-secondary hover:text-teal-900 inline-flex items-center gap-1"
                              title="View Details"
                              aria-label={`View details for ${vendor.business_name}`}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {vendor.is_active ? (
                              <>
                                {deactivateConfirm === vendor.id ? (
                                  <div className="inline-flex gap-2">
                                    <button
                                      onClick={() => handleDeactivate(vendor.id!)}
                                      className="text-red-600 hover:text-red-900 text-xs px-2 py-1 border border-red-600 rounded"
                                    >
                                      Confirm
                                    </button>
                                    <button
                                      onClick={() => setDeactivateConfirm(null)}
                                      className="text-gray-600 hover:text-gray-900 text-xs px-2 py-1 border border-gray-300 rounded"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setDeactivateConfirm(vendor.id!)}
                                    className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                                    title="Deactivate"
                                    aria-label={`Deactivate ${vendor.business_name}`}
                                  >
                                    <UserX className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            {activateConfirm === vendor.id ? (
                              <div className="inline-flex gap-2">
                                <button
                                  onClick={() => handleActivate(vendor.id!)}
                                  className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-600 rounded"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setActivateConfirm(null)}
                                  className="text-gray-600 hover:text-gray-900 text-xs px-2 py-1 border border-gray-300 rounded"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setActivateConfirm(vendor.id!)}
                                className="text-green-600 hover:text-green-900 inline-flex items-center gap-1"
                                title="Activate"
                                aria-label={`Activate ${vendor.business_name}`}
                              >
                                <UserCheck className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-500 italic">
                            No profile created
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {vendors.map((vendor) => (
              <div key={vendor.id ?? `user-${vendor.user_id}`} className="bg-white shadow-sm rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900">
                      {vendor.business_name}
                    </h3>
                    {vendor.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                        {vendor.description}
                      </p>
                    )}
                  </div>
                  <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    vendor.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {vendor.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {vendor.product_types && vendor.product_types.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {vendor.product_types.slice(0, 3).map((type, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      >
                        {type}
                      </span>
                    ))}
                    {vendor.product_types.length > 3 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        +{vendor.product_types.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div className="text-xs text-gray-500 mb-3">
                  Created: {vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : 'N/A'}
                </div>

                <div className="flex gap-2">
                  {vendor.id ? (
                    <>
                      <button
                        onClick={() => handleViewDetails(vendor.id!)}
                        className="flex-1 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm flex items-center justify-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        View Details
                      </button>

                      {vendor.is_active ? (
                        deactivateConfirm === vendor.id ? (
                          <>
                            <button
                              onClick={() => handleDeactivate(vendor.id!)}
                              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeactivateConfirm(null)}
                              className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setDeactivateConfirm(vendor.id!)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center justify-center gap-2"
                          >
                            <UserX className="h-4 w-4" />
                          </button>
                        )
                      ) : (
                        activateConfirm === vendor.id ? (
                          <>
                            <button
                              onClick={() => handleActivate(vendor.id!)}
                              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setActivateConfirm(null)}
                              className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setActivateConfirm(vendor.id!)}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center justify-center gap-2"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                        )
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-gray-500 italic">
                      No profile created
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white shadow-sm rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-600">
                      Items per page:
                    </label>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Vendor Details Modal */}
      {showDetailsModal && selectedVendor && (
        <VendorDetailsModal
          vendorId={selectedVendor}
          onClose={handleCloseModal}
        />
      )}

      {/* Vendor Create Modal */}
      {showCreateModal && (
        <VendorCreateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => loadVendors()}
        />
      )}
    </div>
  );
}
